import React, { useState, useEffect } from 'react';
import { motion } from "motion/react";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import type { Event } from '@/features/clubs/types/types';
import { likeService } from '@/features/likes/services/likeService';
import EventCard from './EventCard';

interface UpcomingEventsSectionProps {
  events: Event[];
  clubHandle: string;
}

const UpcomingEventsSection: React.FC<UpcomingEventsSectionProps> = ({ events, clubHandle }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [scrollDirection, setScrollDirection] = useState<'down' | 'up'>('down');
  const [lastScrollY, setLastScrollY] = useState(0);
  const [likedMap, setLikedMap] = useState<Record<string, boolean>>({});

  // Track scroll direction
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY) {
        setScrollDirection('down');
      } else {
        setScrollDirection('up');
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Fetch like status for events on initial load when user is authenticated
  // Note: Intentionally not re-fetching when isAuthenticated changes mid-session
  // to avoid race conditions with toggle operations
  useEffect(() => {
    const fetchLikeStatus = async () => {
      if (!isAuthenticated || events.length === 0) return;
      
      const eventIds = events.map(event => event.id);
      const response = await likeService.checkLikes({
        entity_ids: eventIds,
        entity_type: 'events',
      });
      setLikedMap(response.liked_map);
    };

    fetchLikeStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  const handleLikeToggle = (eventId: string, newLikedState: boolean) => {
    setLikedMap(prev => ({ ...prev, [eventId]: newLikedState }));
  };

  // Group events into rows (2 cards per row)
  const eventsInRows = [];
  for (let i = 0; i < events.length; i += 2) {
    eventsInRows.push(events.slice(i, i + 2));
  }

  // Dynamic variants based on scroll direction
  const getGridContainerVariants = () => ({
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { 
        staggerChildren: 0.4, 
        delayChildren: 0.1,
        staggerDirection: scrollDirection === 'up' ? -1 : 1
      },
    },
  });

  const rowVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0, delayChildren: 0 },
    },
  };

  const gridItemVariants = {
    hidden: { 
      opacity: 0, 
      y: scrollDirection === 'up' ? -30 : 30, 
      scale: 0.8 
    },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { 
        duration: 0.6, 
        ease: "easeOut" as const,
        scale: { duration: 0.5, ease: "easeOut" as const }
      } 
    },
  };

  const handleEventClick = (event: Event) => {
    navigate(`/venues/${clubHandle}/events/${event.id}`);
  };

  return (
    <section 
      id="events" 
      className="relative bg-black py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="mb-16">
          <h2 className="text-4xl sm:text-5xl font-serif font-medium text-white">Upcoming Events</h2>
        </div>

        {/* Row-based animation layout */}
        <motion.div 
          className="space-y-16"
          variants={getGridContainerVariants()}
          initial="hidden"
          // Animate when the component scrolls into view
          whileInView="visible"
          viewport={{ once: false, amount: 0.1 }}
        >
          {eventsInRows.map((row, rowIndex) => (
            <motion.div 
              key={rowIndex}
              className="grid grid-cols-1 md:grid-cols-2 gap-x-8"
              variants={rowVariants}
            >
              {row.map(event => (
                <motion.div key={event.id} variants={gridItemVariants}>
                  <EventCard 
                    event={event} 
                    onEventClick={handleEventClick}
                    isLiked={likedMap[event.id] || false}
                    onToggleLike={(newState) => handleLikeToggle(event.id, newState)}
                  />
                </motion.div>
              ))}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default UpcomingEventsSection;
